// Dev-only gallery of every transaction-status popup state, so the QA matrix in
// `docs/track-progress-redesign-plan.md` §7 can be reviewed in both themes
// without broadcasting a real transaction on ten different flows.
//
// Mounted ONLY when `isDev` (see `MainRoutes`), and deliberately outside every
// route guard so it renders with no wallet and no keystore. It imports nothing
// but the popup primitives — no controller, no dispatch — so it cannot affect
// the app it lives in.
import React, { useState } from 'react'
import { ScrollView, View } from 'react-native'

import Button from '@common/components/Button'
import Text from '@common/components/Text'
import { ThemeContext } from '@common/contexts/themeContext'
import TrackProgressCard from '@common/components/TrackProgress/TrackProgressCard'
import StatusBody from '@common/components/TrackProgress/StatusBody'
import { RailStep } from '@common/components/TrackProgress/StatusRail'
import useTheme from '@common/hooks/useTheme'
import ThemeColors, { DEFAULT_THEME, THEME_TYPES, ThemeProps } from '@common/styles/themeConfig'
import flexbox from '@common/styles/utils/flexbox'

// Same fold the real ThemeProvider does, so the forced-light preview reads the
// exact token values the app would.
const buildTheme = (type: THEME_TYPES.DARK | THEME_TYPES.LIGHT): ThemeProps =>
  Object.fromEntries(
    Object.entries(ThemeColors).map(([key, value]) => [
      key,
      (value as any)[type] || (value as any)[DEFAULT_THEME]
    ])
  ) as ThemeProps

type Case = {
  name: string
  steps: RailStep[]
  title: string
  subtitle: string
  rows?: { label: string; value: string }[]
  explorerLink?: string
}

const CASES: Case[] = [
  {
    name: 'Top-up · confirming',
    steps: [
      { label: 'Signed', state: 'done' },
      { label: 'Confirmed', activeLabel: 'Confirming', state: 'active' },
      { label: 'Credited', state: 'pending' }
    ],
    title: 'Confirming your top-up',
    subtitle: 'Almost there!',
    rows: [
      { label: 'Amount', value: '25.00 USDC' },
      { label: 'Network', value: 'BNB Chain' },
      { label: 'Gas Tank balance', value: '$4.81' }
    ]
  },
  {
    name: 'Top-up · settling',
    steps: [
      { label: 'Signed', state: 'done' },
      { label: 'Confirmed', state: 'done' },
      { label: 'Credited', activeLabel: 'Settling', state: 'active' }
    ],
    title: 'Top up ready!',
    subtitle: 'Waiting for the relayer to credit your deposit.',
    rows: [
      { label: 'Amount', value: '25.00 USDC' },
      { label: 'Network', value: 'BNB Chain' },
      { label: 'Gas Tank balance', value: '$4.81' },
      { label: 'Settling', value: '+25.00 USDC' }
    ],
    explorerLink: 'https://bscscan.com/tx/0xabc'
  },
  {
    name: 'Top-up · credited',
    steps: [
      { label: 'Signed', state: 'done' },
      { label: 'Confirmed', state: 'done' },
      { label: 'Credited', state: 'done' }
    ],
    title: 'Top up ready!',
    subtitle: 'You can now use your gas tank',
    rows: [
      { label: 'Amount', value: '25.00 USDC' },
      { label: 'Network', value: 'BNB Chain' },
      { label: 'Gas Tank balance', value: '$29.81' }
    ],
    explorerLink: 'https://bscscan.com/tx/0xabc'
  },
  {
    name: 'Send · confirming (2 nodes)',
    steps: [
      { label: 'Signed', state: 'done' },
      { label: 'Confirmed', activeLabel: 'Confirming', state: 'active' }
    ],
    title: 'Confirming your transfer',
    subtitle: 'Almost there!',
    rows: [{ label: 'Network', value: 'Base' }]
  },
  {
    name: 'Send · failed',
    steps: [
      { label: 'Signed', state: 'done' },
      { label: 'Confirmed', state: 'failed' }
    ],
    title: 'Something went wrong!',
    subtitle: "We couldn't complete your transfer."
  },
  {
    name: 'Private send · proof',
    steps: [
      { label: 'Proof', activeLabel: 'Proving', state: 'active' },
      { label: 'Sent', state: 'pending' }
    ],
    title: 'Generating transaction proof...',
    subtitle: 'Keep this window open. Closing it may interrupt the process.',
    rows: [
      { label: 'Amount', value: '0.5 WETH' },
      { label: 'To', value: '0zk1qyql8…7m4rv3' },
      { label: 'Network', value: 'Ethereum' }
    ]
  },
  {
    name: 'Private send · failed at proof',
    steps: [
      { label: 'Proof', state: 'failed' },
      { label: 'Sent', state: 'pending' }
    ],
    title: 'Something went wrong!',
    subtitle: 'Failed to generate private transfer proof'
  },
  {
    name: 'Bridge · in progress (3 nodes)',
    steps: [
      { label: 'Signed', state: 'done' },
      { label: 'Confirmed', state: 'done' },
      { label: 'Delivered', activeLabel: 'Delivering', state: 'active' }
    ],
    title: 'Bridging your tokens',
    subtitle: 'Safe to close this screen — the bridge keeps going.',
    rows: [
      { label: 'From', value: '100.00 USDC · Base' },
      { label: 'To', value: '99.42 USDC · Arbitrum' },
      { label: 'Time', value: '~ 2m' }
    ]
  },
  {
    name: 'Bridge · refunded',
    steps: [
      { label: 'Signed', state: 'done' },
      { label: 'Confirmed', state: 'done' },
      { label: 'Refunded', state: 'refunded' }
    ],
    title: 'Bridge refunded',
    subtitle: '100.00 USDC was refunded to your account as the bridge failed.',
    rows: [
      { label: 'From', value: '100.00 USDC · Base' },
      { label: 'To', value: '99.42 USDC · Arbitrum' }
    ],
    explorerLink: 'https://basescan.org/tx/0xabc'
  },
  {
    name: 'Gasless · settling (relayer)',
    steps: [
      { label: 'Signed', state: 'done' },
      { label: 'Submitted', state: 'done' },
      { label: 'Delivered', activeLabel: 'Delivering', state: 'active' }
    ],
    title: 'Submitted!',
    subtitle: 'The relayer is settling your gasless swap. Safe to close this screen.'
  },
  {
    name: 'Gasless · relayer failed',
    steps: [
      { label: 'Signed', state: 'done' },
      { label: 'Submitted', state: 'done' },
      { label: 'Delivered', state: 'failed' }
    ],
    title: 'Submitted!',
    subtitle: 'The relayer could not settle your gasless swap.'
  },
  {
    name: 'Permit · unwrapping (3 nodes)',
    steps: [
      { label: 'Signed', state: 'done' },
      { label: 'Filled', state: 'done' },
      { label: 'Unwrapped', activeLabel: 'Unwrapping', state: 'active' }
    ],
    title: 'Unwrapping WPLS to PLS...',
    subtitle: 'Almost there!'
  },
  {
    name: 'Private swap · submitted',
    steps: [
      { label: 'Proof', state: 'done' },
      { label: 'Submitted', state: 'done' }
    ],
    title: 'Nice trade!',
    subtitle: 'Swap submitted. A relayer will deliver your funds shortly.',
    rows: [
      { label: 'From', value: '1.5 WETH' },
      { label: 'To', value: 'USDC' },
      { label: 'Network', value: 'Ethereum' }
    ]
  },
  {
    name: 'Swap same-chain · done',
    steps: [
      { label: 'Signed', state: 'done' },
      { label: 'Confirmed', state: 'done' }
    ],
    title: 'Nice trade!',
    subtitle: 'USDC delivered - like magic.',
    rows: [
      { label: 'From', value: '100.00 PLS' },
      { label: 'To', value: '0.98 USDC' },
      { label: 'Network', value: 'PulseChain' },
      { label: 'Time', value: 'instant' }
    ],
    explorerLink: 'https://scan.pulsechain.com/tx/0xabc'
  },
  {
    name: 'Swap same-chain · failed before signing',
    steps: [
      { label: 'Signed', state: 'failed' },
      { label: 'Filled', state: 'pending' }
    ],
    title: 'Swap failed',
    subtitle: 'Insufficient allowance for Permit2.'
  },
  {
    name: 'Swap same-chain · failed after signing',
    steps: [
      { label: 'Signed', state: 'done' },
      { label: 'Filled', state: 'failed' }
    ],
    title: 'Swap failed',
    subtitle: 'Failed to submit order'
  },
  {
    name: 'CCTP · on its way (2 nodes)',
    steps: [
      { label: 'Burned', state: 'done' },
      { label: 'Submitted', state: 'done' }
    ],
    title: 'On its way',
    subtitle: 'Cross-chain USDC on its way - like magic.'
  }
]

type GalleryProps = {
  only: number | null
  setOnly: (i: number | null) => void
  forceLight: boolean
  setForceLight: (v: boolean) => void
}

const Gallery = ({ only, setOnly, forceLight, setForceLight }: GalleryProps) => {
  const { theme, themeType } = useTheme()

  const visible = only === null ? CASES : [CASES[only] as Case]

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.onboardingPageBackground }}
      contentContainerStyle={{ padding: 24 }}
      testID="track-progress-gallery"
    >
      <Text fontSize={18} weight="semiBold" appearance="primaryText" style={{ marginBottom: 4 }}>
        TrackProgress gallery
      </Text>
      <Text fontSize={13} appearance="secondaryText" style={{ marginBottom: 24 }}>
        {`theme: ${themeType} · ${CASES.length} cases`}
      </Text>

      <View style={[flexbox.directionRow, { flexWrap: 'wrap', gap: 8, marginBottom: 24 }]}>
        <Text
          fontSize={12}
          appearance="primary"
          onPress={() => setOnly(null)}
          testID="gallery-case-all"
        >
          [all]
        </Text>
        <Text
          fontSize={12}
          appearance="primary"
          onPress={() => setForceLight(!forceLight)}
          testID="gallery-toggle-theme"
        >
          [theme]
        </Text>
        {CASES.map((c, i) => (
          <Text
            key={c.name}
            fontSize={12}
            appearance="secondaryText"
            onPress={() => setOnly(i)}
            testID={`gallery-case-${i}`}
          >
            {`[${i}]`}
          </Text>
        ))}
      </View>

      {visible.map((c) => (
        <View key={c.name} style={{ marginBottom: 40 }}>
          <Text
            fontSize={13}
            weight="medium"
            appearance="secondaryText"
            style={{ marginBottom: 8 }}
          >
            {c.name}
          </Text>
          <TrackProgressCard
            footer={
              // The REAL buttons, not a hand-rolled copy: a mock footer here
              // once showed black label text on the primary button, which the
              // shipped wrapper never does. A QA page that lies about the design
              // system is worse than no QA page.
              <View style={[flexbox.directionRow, { gap: 12, width: '100%' }]}>
                <View
                  style={[
                    flexbox.center,
                    {
                      flex: 1,
                      height: 48,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: theme.accentChipDecorative,
                      backgroundColor: theme.accentChipBackground
                    }
                  ]}
                >
                  <Text
                    fontSize={16}
                    weight="medium"
                    color={
                      themeType === THEME_TYPES.DARK ? theme.white : theme.accentChipDecorative
                    }
                  >
                    Add more
                  </Text>
                </View>
                <Button
                  text="Close"
                  type="primary"
                  hasBottomSpacing={false}
                  onPress={() => {}}
                  style={{ flex: 1, height: 48, borderRadius: 8, marginLeft: 0 }}
                />
              </View>
            }
          >
            <StatusBody
              steps={c.steps}
              title={c.title}
              subtitle={c.subtitle}
              rows={c.rows}
              explorerLink={c.explorerLink}
              openExplorerText={c.explorerLink ? 'View transaction' : undefined}
            />
          </TrackProgressCard>
        </View>
      ))}
    </ScrollView>
  )
}

const TrackProgressGalleryScreen = () => {
  const [only, setOnly] = useState<number | null>(null)
  const [forceLight, setForceLight] = useState(false)
  const parent = useTheme()

  const inner = (
    <Gallery only={only} setOnly={setOnly} forceLight={forceLight} setForceLight={setForceLight} />
  )

  if (!forceLight) return inner

  // Override the context rather than the app's stored setting, so the light
  // pass needs no wallet and leaves the user's theme preference alone.
  return (
    <ThemeContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        ...parent,
        theme: buildTheme(THEME_TYPES.LIGHT),
        themeType: THEME_TYPES.LIGHT,
        selectedThemeType: THEME_TYPES.LIGHT
      }}
    >
      {inner}
    </ThemeContext.Provider>
  )
}

export default TrackProgressGalleryScreen
